/**
 * URL and domain validation utilities
 */

/**
 * Validates a domain string (without protocol) using regex
 * Ensures domain follows proper format with valid characters and structure
 *
 * @param domain Domain string to validate
 * @returns boolean indicating if domain is valid
 */
export const validateDomain = (domain: string): boolean => {
  if (!domain) return false;

  // Strip any leading/trailing whitespace
  const trimmedDomain = domain.trim();

  // Basic domain validation with regex
  // This checks for valid domain structure:
  // - No @ symbols or special characters (validates characters before and after dots)
  // - Properly formatted with at least one dot
  // - No consecutive dots
  // - Valid TLD (at least 2 characters)
  const domainRegex =
    /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(trimmedDomain);
};

/**
 * Validates a URL string (with or without protocol)
 * Combines domain validation with URL constructor validation
 *
 * @param url URL string to validate
 * @returns boolean indicating if URL is valid
 */
export const validateURL = (url: string): boolean => {
  if (!url) return false;

  // Strip any leading/trailing whitespace
  const trimmedUrl = url.trim();

  // For our specific requirements, we don't want URLs with @ symbols
  // This is a more reliable check than relying on URL constructor behavior
  if (trimmedUrl.includes('@')) {
    return false;
  }

  try {
    // First attempt to use URL constructor to parse the URL
    let urlObj: URL;

    // Check if it already has http/https
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      urlObj = new URL(trimmedUrl);
    } else {
      urlObj = new URL(`https://${trimmedUrl}`);
    }

    // Extract only the hostname for domain validation
    const hostname = urlObj.hostname;

    // Validate the hostname part (this will reject hostnames with @ symbols)
    if (!validateDomain(hostname)) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

export default validateURL;
